import { apiRequest } from './api';
import type { CartItem } from '../context/CartContext';

interface SyncCartOptions {
  items: CartItem[];
  tipCents?: number;
  tipReleaseId?: number;
  tipArtistId?: number;
  tipArtistSlug?: string;
  token?: string | null;
  allowEmpty?: boolean;
}

/**
 * Pushes the current client cart to the API so tip routing and checkout use the same snapshot.
 */
export async function syncCartWithServer({
  items,
  tipCents = 0,
  tipReleaseId,
  tipArtistId,
  tipArtistSlug,
  token,
  allowEmpty = false,
}: SyncCartOptions): Promise<number> {
  if (items.length === 0 && tipCents <= 0 && !allowEmpty) {
    throw new Error('Your cart is empty.');
  }

  const authToken = token ?? undefined;
  let cartId = 0;
  const purchaseItems = items.filter((item) => item.kind !== 'tip');
  const tips = items.filter((item) => item.kind === 'tip');
  const artistTips = tips.filter((tip) => tip.tipSource === 'artist' || !tip.tipForItemId);
  const purchaseKey = (item: CartItem) =>
    item.trackId
      ? `track-${item.trackId ?? item.id}`
      : `release-${item.releaseId ?? item.id}`;

  const tipTotalsByTarget = tips
    .filter((tip) => tip.tipSource !== 'artist')
    .reduce<Map<string, number>>((map, tip) => {
      const quantity = Math.max(1, tip.quantity ?? 1);
      const increment = Math.round(tip.price * quantity);

      const targetKey =
        tip.tipForItemId ??
        (tip.trackId !== undefined && tip.trackId !== null
          ? `track-${tip.trackId}`
          : tip.releaseId !== undefined && tip.releaseId !== null
            ? `release-${tip.releaseId}`
            : null);

      if (!targetKey) return map;

      const current = map.get(targetKey) ?? 0;
      map.set(targetKey, current + increment);
      return map;
    }, new Map());

  const cartResponse = await apiRequest<{
    cart: { cart_id: number; items: { id?: number; cart_item_id?: number }[] } | null;
  }>('/cart', {
    authToken,
  });
  const remoteItems = cartResponse.cart?.items ?? [];
  cartId = cartResponse.cart?.cart_id ?? 0;

  if (items.length === 0 && tipCents <= 0) {
    if (cartId && remoteItems.length > 0) {
      await Promise.all(
        remoteItems
          .map((item) => item.cart_item_id ?? item.id)
          .filter((id): id is number => typeof id === 'number')
          .map((id) =>
            apiRequest(`/cart/items/${id}`, {
              method: 'DELETE',
              authToken,
            })
          )
      );
    }
    return cartId;
  }

  if (cartId && remoteItems.length > 0) {
    await Promise.all(
      remoteItems
        .map((item) => item.cart_item_id ?? item.id)
        .filter((id): id is number => typeof id === 'number')
        .map((id) =>
          apiRequest(`/cart/items/${id}`, {
            method: 'DELETE',
            authToken,
          })
        )
    );
  }

  if (!cartId && items.length > 0) {
    throw new Error('Unable to sync your cart. Please try again.');
  }

  for (const item of purchaseItems) {
    const releaseId = Number(item.releaseId);
    if (item.kind !== 'tip') {
      if (!Number.isFinite(releaseId) || releaseId <= 0) {
        throw new Error('Invalid cart item detected. Please remove it and try again.');
      }
      await apiRequest('/cart/items', {
        method: 'POST',
        authToken,
        body: JSON.stringify({
          release_id: releaseId,
          track_id: item.kind === 'track' ? item.trackId : undefined,
          unit_price_cents: Math.round(item.price),
          quantity: Math.max(1, item.quantity),
          tip_cents: tipTotalsByTarget.get(purchaseKey(item)) ?? 0,
        }),
      });
    }
  }

  for (const tip of artistTips) {
    await apiRequest('/cart/items', {
      method: 'POST',
      authToken,
      body: JSON.stringify({
        item_type: 'tip',
        release_id: undefined,
        tip_cents: Math.round(tip.price),
        tip_artist_id: tip.artistId,
        tip_artist_slug: tip.artistSlug,
        quantity: Math.max(1, tip.quantity ?? 1),
      }),
    });
  }

  const hasArtistTipItems = artistTips.length > 0;
  if (!hasArtistTipItems && tipCents > 0) {
    await apiRequest('/cart/items', {
      method: 'POST',
      authToken,
      body: JSON.stringify({
        item_type: 'tip',
        release_id: undefined,
        tip_cents: Math.max(0, Math.round(tipCents)),
        tip_artist_id: tipArtistId,
        tip_artist_slug: tipArtistSlug,
        quantity: 1,
      }),
    });
    if (!cartId) {
      const refresh = await apiRequest<{ cart: { cart_id: number } }>('/cart', {
        authToken,
      });
      cartId = refresh.cart?.cart_id ?? 0;
    }
  }

  if (!cartId) {
    throw new Error('Unable to sync your cart. Please try again.');
  }

  return cartId;
}
